/* quickshop
   ========================================================================== */

import { MODAL_LOAD_EVENT, MODAL_CLOSE_EVENT } from '../dialogs/lib';
import { renderProductCta } from '../product/cta/ProductCta';
import { ADD_TO_CART, PRODUCT_STATE_CHANGE } from '../../events';
import Dialogs from '../dialogs/a11y-dialog-component';
import { isDesktopDisplay, desktopMediaQueryListener } from '../mobile/check';
import analytics from '../../analytics';

/**
 * Reads the tracking object from the [data-product-cta] element in the quickshop panel.
 * The tracking data is embedded via format_product_json → selectedVariant.tracking.
 */
function getQuickshopTracking(quickshopEl) {
  try {
    const ctaEl = quickshopEl.querySelector('[data-product-cta]');
    if (!ctaEl?.dataset.product) return null;
    const product = JSON.parse(ctaEl.dataset.product);
    return product.selectedVariant?.tracking ?? null;
  } catch {
    return null;
  }
}

function getQuickshopSizeLabel(sizeInput) {
  return sizeInput.closest('.quickshop__size-label');
}

function isQuickshopSizeVisibleInList(sizeInput) {
  const wrapper = sizeInput.closest('.quickshop__sizes-wrapper');
  const sizeLabel = getQuickshopSizeLabel(sizeInput);

  if (!wrapper || !sizeLabel) return true;

  const wrapperRect = wrapper.getBoundingClientRect();
  const labelRect = sizeLabel.getBoundingClientRect();

  return labelRect.top >= wrapperRect.top && labelRect.bottom <= wrapperRect.bottom;
}

/**
 * Scrolls within the sizes list on modal open only
 */
function scrollQuickshopSizeIntoList(sizeInput) {
  const wrapper = sizeInput.closest('.quickshop__sizes-wrapper');
  const sizeLabel = getQuickshopSizeLabel(sizeInput);

  if (!wrapper || !sizeLabel) return;

  const wrapperRect = wrapper.getBoundingClientRect();
  const labelRect = sizeLabel.getBoundingClientRect();

  if (labelRect.top < wrapperRect.top) {
    wrapper.scrollTop -= wrapperRect.top - labelRect.top;
  } else if (labelRect.bottom > wrapperRect.bottom) {
    wrapper.scrollTop += labelRect.bottom - wrapperRect.bottom;
  }
}

export default function updateQuickshops() {
  document.addEventListener('change', (e) => {
    const trigger = e.target.closest('[data-quickshop-item-trigger]');
    const form = trigger?.closest('[data-quickshop-add-product-form]');
    if (!trigger || !form) return;

    document.dispatchEvent(
      new CustomEvent(PRODUCT_STATE_CHANGE, {
        detail: {
          productHash: form.dataset.productHash,
          currentItemHash: trigger.dataset.quickshopItemTrigger,
        },
      }),
    );
  });

  // Desktop multi-size: uncheck size radios after each add attempt (see ajaxAddProduct ADD_TO_CART in finally)
  document.addEventListener(ADD_TO_CART, (e) => {
    if (!isDesktopDisplay()) return;

    const form = e.detail?.button?.closest('[data-quickshop-add-product-form]');
    if (!form) return;

    const sizeInputs = form.querySelectorAll('[data-quickshop-item-trigger]');
    if (sizeInputs.length <= 1) return;

    sizeInputs.forEach((input) => {
      input.checked = false;
    });
  });

  // Close the AJAX modal when resizing to desktop (quickshop uses CSS hover on desktop)
  desktopMediaQueryListener.addEventListener('change', () => {
    if (isDesktopDisplay()) {
      Dialogs.hide('ajax-modal');
    }
  });

  const ajaxModal = document.getElementById('ajax-modal');

  ajaxModal?.addEventListener(MODAL_LOAD_EVENT, (e) => {
    if (e.detail.role !== 'quickshop') return;

    const quickshop = e.target;
    const form = quickshop.querySelector('[data-quickshop-add-product-form]');
    if (form) form.dataset.modalId = 'ajax-modal';

    renderProductCta({ container: quickshop });

    const tracking = getQuickshopTracking(quickshop);
    if (tracking) {
      analytics.quickshopViewed(tracking);
    }

    // Mobile modal only: keep logical size order, scroll to the pre-selected first available size
    if (!isDesktopDisplay()) {
      const firstAvailable =
        quickshop.querySelector('input[data-quickshop-item-trigger]:checked:not([disabled])') ??
        quickshop.querySelector('input[data-quickshop-item-trigger]:not([disabled])');

      if (firstAvailable) {
        if (firstAvailable.type === 'radio' && !firstAvailable.checked) {
          firstAvailable.checked = true;
        }

        requestAnimationFrame(() => {
          firstAvailable.dispatchEvent(new Event('change', { bubbles: true }));

          if (firstAvailable.type === 'radio' && !isQuickshopSizeVisibleInList(firstAvailable)) {
            scrollQuickshopSizeIntoList(firstAvailable);
          }
        });
      }
    }
  });

  // MODAL_CLOSE_EVENT is dispatched before the modal content is cleared,
  // so [data-product-cta] is still accessible here.
  ajaxModal?.addEventListener(MODAL_CLOSE_EVENT, (e) => {
    if (e.detail.role !== 'quickshop') return;

    const tracking = getQuickshopTracking(e.target);
    if (tracking) {
      analytics.quickshopClosed(tracking);
    }
  });
}
